/*
 * Panel de recomendaciones del carrito.
 *
 * El drawer se renderiza con un <cart-recommendations> vacío que sólo lleva
 * un data-url apuntando a la API de recomendaciones de Shopify. Este elemento
 * pide esa URL y se hidrata con las filas que devuelve.
 *
 * Cada vez que el carrito cambia, Dawn vuelve a pintar .drawer__inner, así que
 * este elemento se destruye y se recrea: la recarga es automática y siempre
 * queda alineado con lo que hay en el carrito.
 */

class CartRecommendations extends HTMLElement {
  connectedCallback() {
    this.loadRecommendations();
  }

  async loadRecommendations() {
    const url = this.dataset.url;
    if (!url || this.dataset.loaded === 'true') return;

    this.dataset.loaded = 'true';

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const incoming = new DOMParser()
        .parseFromString(await response.text(), 'text/html')
        .querySelector('cart-recommendations');

      // Sin recomendaciones el panel se queda oculto y el drawer luce normal.
      if (!incoming || incoming.children.length === 0) {
        this.collapse();
        return;
      }

      this.innerHTML = incoming.innerHTML;
      this.hidden = false;
    } catch (error) {
      console.error('cart-recommendations:', error);
      this.collapse();
    }
  }

  /*
   * Devuelve el drawer a una sola columna. El servidor pinta la clase asumiendo
   * que habrá recomendaciones; si no llegaron, hay que retirarla o quedaría una
   * columna vacía en escritorio.
   */
  collapse() {
    const drawerInner = this.closest('.drawer__inner');
    if (drawerInner) drawerInner.classList.remove('drawer__inner--has-recommendations');
  }
}

customElements.define('cart-recommendations', CartRecommendations);

/*
 * Agregar al carro desde el panel.
 *
 * Se delega en document porque el panel se reemplaza entero en cada refresco.
 * Reutiliza renderContents() de Dawn para repintar drawer y burbuja del carrito.
 */
document.addEventListener('click', (event) => {
  const button = event.target.closest('.cart-recommendations__add');
  if (!button) return;

  event.preventDefault();
  addRecommendedVariant(button);
});

async function addRecommendedVariant(button) {
  const variantId = button.dataset.variantId;
  if (!variantId || button.getAttribute('aria-busy') === 'true') return;

  const cartDrawer = document.querySelector('cart-drawer');
  button.setAttribute('aria-busy', 'true');

  const sections = cartDrawer ? cartDrawer.getSectionsToRender().map((section) => section.id) : [];

  try {
    const response = await fetch(`${routes.cart_add_url}.js`, {
      ...fetchConfig('javascript'),
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
        sections: sections,
        sections_url: window.location.pathname,
      }),
    });

    let parsedState = await response.json();
    if (parsedState.status) throw new Error(parsedState.description || parsedState.message);

    // Respaldo Napibela: ver napibelaCompletarSecciones en global.js.
    if (typeof napibelaCompletarSecciones === 'function') {
      parsedState = await napibelaCompletarSecciones(parsedState, sections);
    }
    if (cartDrawer && parsedState.sections) cartDrawer.renderContents(parsedState);

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-recommendations', productVariantId: variantId });
    }
  } catch (error) {
    console.error('cart-recommendations:', error);
  } finally {
    button.removeAttribute('aria-busy');
  }
}
